import { keepPreviousData } from '@tanstack/query-core'
import { InfiniteData } from '@tanstack/react-query'
import queryString from 'query-string'
import { useDispatch } from 'react-redux'
import { ExpenseReportsFiltersBody } from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import {
  ExpenseReportModel,
  ExpenseReportResponse,
} from 'src/models/expenseReport.model'
import { PaginationModel } from 'src/models/pagination.models'
import { oopeQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  useExtendInfiniteQuery,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { GET_EXPENSE_REPORT_FAILURE, GET_EXPENSE_REPORT_INIT, GET_EXPENSE_REPORT_SUCCESS } from 'src/redux/actions/data/expenses.actions'

export interface ExpenseReportsSuccessPayload {
  expenseReports: ExpenseReportResponse[]
  pagination: PaginationModel
}

interface GetExpenseReportsDataModel {
  expenseReports: ExpenseReportModel[] | undefined
  page: number | undefined
  numberOfPages: number | undefined
}

const selectExpenseReportsListModel = (
  response: InfiniteData<ExpenseReportsSuccessPayload, number>,
): GetExpenseReportsDataModel => {
  return {
    expenseReports: response?.pages.flatMap((page) =>
      page.expenseReports.map((expReport) => new ExpenseReportModel(expReport)),
    ),
    page: response?.pages[0]?.pagination.page,
    numberOfPages: response?.pages[0]?.pagination.numberOfPages,
  }
}

interface UseQueryExpenseReportsOptions
  extends ExtendBaseInfiniteQueryOptions<
    ExpenseReportsSuccessPayload,
    number,
    GetExpenseReportsDataModel
  > {
  filters: ExpenseReportsFiltersBody | undefined
}

export const useQueryApproverOOPEs = ({
  filters,
  enabled = true,
  perPage = 50,
  ...rest
}: UseQueryExpenseReportsOptions) => {
  return useExtendInfiniteQuery({
    queryKey: oopeQueryKeys.approverList(filters),
    queryUrl: ({ pageParam }) => {
      return `/expensereports/approver?${queryString.stringify({ ...filters, page: pageParam, perPage })}`
    },
    enabled: enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, __) =>
      getNextExtendPageParam(lastPage.pagination),
    select: selectExpenseReportsListModel,
    // keepPreviousData is used to ensure that when the user scrolls down and the next page is fetched,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...rest,
  })
}

export const useQuerySubmitterOOPEs = ({
  filters,
  enabled = true,
  perPage = 50,
  ...rest
}: UseQueryExpenseReportsOptions) => {
  return useExtendInfiniteQuery({
    queryKey: oopeQueryKeys.submitterList(filters),
    queryUrl: ({ pageParam }) => {
      return `/expensereports/submitter?${queryString.stringify({ ...filters, page: pageParam, perPage })}`
    },
    enabled: enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, __) =>
      getNextExtendPageParam(lastPage.pagination),
    select: selectExpenseReportsListModel,
    // keepPreviousData is used to ensure that when the user scrolls down and the next page is fetched,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...rest,
  })
}

export const useQueryNeedsManagerApprovalOOPEs = ({
  filters,
  enabled = true,
  perPage = 50,
  ...rest
}: UseQueryExpenseReportsOptions) => {
  return useExtendInfiniteQuery({
    queryKey: oopeQueryKeys.directReportsList(filters),
    queryUrl: ({ pageParam }) => {
      return `/expensereports/directreports?${queryString.stringify({ ...filters, page: pageParam, perPage })}`
    },
    enabled: enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, __) =>
      getNextExtendPageParam(lastPage.pagination),
    select: selectExpenseReportsListModel,
    // keepPreviousData is used to ensure that when the user scrolls down and the next page is fetched,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...rest,
  })
}

export const useQueryOopeNeedsManagerApprovalCount = ({
  enabled,
  ...rest
}: ExtendBaseQueryOptions<{ count: number }, number>) => {
  return useExtendQuery({
    queryKey: oopeQueryKeys.count(),
    queryUrl: () => `/expensereports/directreports/count`,
    select: (data) => data.count,
    enabled: enabled,
    ...rest,
  })
}

interface QueryVirtualCardDetailOptions
  extends ExtendBaseQueryOptions<
    ExpenseReportResponse,
    ExpenseReportResponse
  > {
  reportId: string | undefined
}

export const useQueryOOPEReportDetail = ({
  reportId,
  enabled = true,
  ...rest
}: QueryVirtualCardDetailOptions) => {
  const dispatch = useDispatch()

  return useExtendQuery<
    ExpenseReportResponse,
    ExpenseReportResponse
  >({
    queryKey: oopeQueryKeys.reportDetails(reportId),
    queryUrl: () => `/expensereports/${reportId}`,
    enabled: enabled && reportId !== undefined,
    ...rest,
    // redux <-> react query interop
    reduxInitAction: { type: GET_EXPENSE_REPORT_INIT },
    onSuccess: (data) => {
      dispatch({ type: GET_EXPENSE_REPORT_SUCCESS, payload: data })
    },
    onError: (error) => {
      dispatch({ type: GET_EXPENSE_REPORT_FAILURE, payload: error })
    },
  })
}
